import React, { useMemo } from "react"
import { deriveSectionSurfaceTone } from "~/helpers/colorSurface"
import { SectionSurfaceToneContext } from "./SectionSurfaceTone"

type BorderStyle = "Wave" | "Wave-2" | "Arc"

export interface Props {
  backgroundColor?: string
  backgroundImageUrl?: string
  topBorderStyle?: BorderStyle | null
  bottomBorderStyle?: BorderStyle | null
  /** Extra bottom padding on the section root (painted with section background). */
  paddingBottom?: string
  children?: React.ReactNode
}

interface SvgProps {
  fill?: string
}

const WaveSvg = ({ fill }: SvgProps) => (
  <svg
    className="block w-full h-full max-w-none"
    width="1440"
    height="28"
    viewBox="0 0 1440 28"
    preserveAspectRatio="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1440 27.5884C1351.73 12.9344 1247.95 -6.20873 1146.46 1.95259C1057.11 9.13923 984.877 25.9651 888.872 24.6037C762.542 22.8115 679.224 -4.93739 570.012 2.88668C460.8 10.7125 452.242 22.8909 328.676 26.5867C202.5 26.5867 139.521 3.44455 0 11.7825V35.6806H1440L1440 27.5884Z"
      fill={fill}
    />
  </svg>
)

const Wave2Svg = ({ fill }: SvgProps) => (
  <svg
    className="block w-full h-full max-w-none"
    width="1440"
    height="58"
    viewBox="0 0 1440 58"
    preserveAspectRatio="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.52995e-05 44.8164C88.2686 23.568 192.047 -2.73954 293.539 9.09439C382.895 19.515 455.123 43.9126 551.128 41.9385C677.458 39.3398 760.775 -0.896089 869.988 10.4488C979.2 21.7963 987.758 39.455 1111.32 44.8138C1234.89 50.1726 1300.48 -10.4923 1440 1.59769V58H-6.10352e-05L4.52995e-05 44.8164Z"
      fill={fill}
    />
  </svg>
)

const ArcSvg = ({ fill }: SvgProps) => (
  <svg
    className="block w-full h-full max-w-none"
    width="1440"
    height="28"
    viewBox="0 0 1440 28"
    preserveAspectRatio="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M-377 129.591C-377 129.591 -44.8528 0 720 0C1484.85 0 1817 129.591 1817 129.591V158H-377V129.591Z"
      fill={fill}
    />
  </svg>
)

// Each border wraps its SVG in a sized container so the layout space is
// reserved by the wrapper (aspect-ratio + width), independent of when the
// inner SVG paints or measures. Acts as a fallback skeleton for the SVG.
function renderBorder(style: BorderStyle, fill?: string) {
  switch (style) {
    case "Wave":
      return (
        <div
          className="block w-full"
          style={{ aspectRatio: "1440 / 28" }}
          aria-hidden
        >
          <WaveSvg fill={fill} />
        </div>
      )
    case "Wave-2":
      return (
        <div
          className="block w-full"
          style={{ aspectRatio: "1440 / 58" }}
          aria-hidden
        >
          <Wave2Svg fill={fill} />
        </div>
      )
    case "Arc":
      return (
        <div className="w-full overflow-hidden" aria-hidden>
          <div
            className="relative left-1/2 -translate-x-1/2 w-[140vw] min-w-[140vw] max-w-none"
            style={{ aspectRatio: "1440 / 28" }}
          >
            <ArcSvg fill={fill} />
          </div>
        </div>
      )
  }
}

export function Section({
  backgroundColor,
  backgroundImageUrl,
  topBorderStyle,
  bottomBorderStyle,
  paddingBottom,
  children
}: Props) {
  // Top border is absolutely positioned; margin on children does not separate
  // content from the wave because the anchor was tied to section height.
  // Anchor the border to the top edge (bottom-full) and reserve space with
  // padding-top = (viewBox height / 1440) * 100% of section width.
  const topContentPaddingTop =
    topBorderStyle === "Wave"
      ? `calc(${(28 / 1440) * 100}% + 1.5rem)`
      : topBorderStyle === "Wave-2"
        ? `${(58 / 1440) * 100}%`
        : undefined
  const arcTopPaddingClass =
    topBorderStyle === "Arc"
      ? "pt-[calc(1.944%+2rem)] md:pt-[1.944%]"
      : undefined

  const surfaceTone = useMemo(
    () => deriveSectionSurfaceTone(backgroundColor),
    [backgroundColor]
  )

  return (
    <SectionSurfaceToneContext.Provider value={surfaceTone}>
      <div
        className="relative overflow-visible z-0"
        style={{
          backgroundColor,
          backgroundImage: backgroundImageUrl
            ? `url(${backgroundImageUrl})`
            : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
          ...(paddingBottom ? { paddingBottom } : {})
        }}
      >
        {topBorderStyle && (
          <div className="absolute bottom-[99.9%] left-0 right-0 z-10 w-full pointer-events-none leading-none">
            {renderBorder(topBorderStyle, backgroundColor)}
          </div>
        )}
        <div
          className={`relative z-20 flow-root ${arcTopPaddingClass ?? ""}`}
          style={
            topContentPaddingTop
              ? { paddingTop: topContentPaddingTop }
              : undefined
          }
        >
          {children}
        </div>
        {bottomBorderStyle && (
          <div className="absolute left-0 right-0 top-[99.5%] w-full pointer-events-none z-10 leading-none rotate-180">
            {renderBorder(bottomBorderStyle, backgroundColor)}
          </div>
        )}
      </div>
    </SectionSurfaceToneContext.Provider>
  )
}
